import { Badge, Box } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertComponent,
  BreadcrumbComponent,
  PaginationComponent,
  SearchInputComponent,
} from "../../../common/components";
import { useLocation, useNavigate } from "react-router-dom";
import { routes } from "../../../constants/routes";
import { AlertMessage } from "../tenant-details/tenant-details-types";
import _ from "lodash";
import icons from "../../../assets/icons/icons";
import "./leads-log.scss";
import MUIBoxTable from "../../../common/components/mui-table/mui-box-table";
import StatusBadge from "./status-badge";
import { formatDate } from "../../../utils/utils";
import LeadsLogFilters from "./leads-log-filters/leads-log-filters";
import { getLeads } from "../../../api/lead";
import LeadView from "../../leads/lead-view/lead-view";
import dayjs from "dayjs";
import TooltipIcon from "../../../assets/icons/tooltip-icon.svg";
import CloseIcon from "@mui/icons-material/Close";
import { useSetLoading } from "../../../common/hooks/appearance";
import LeadsStatus from "../../leads/leads-status";

interface SortOption {
  sortDirection: string | null;
  sortBy: string | null;
}
const LeadsLog: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const { tenantData } = state || {};

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);
  const [searchText, setSearchText] = useState<string>("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [leadData, setLeadData] = useState<any[]>([]);
  const [sortOption, setSortOption] = useState<SortOption>({
    sortDirection: null,
    sortBy: null,
  });
  const [alertMessage, setAlertMessage] = React.useState<AlertMessage>({
    open: false,
    message: "",
    type: undefined,
  });
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [viewOpen, setViewOpen] = useState<boolean>(false);
  const [selectedLeadId, setSelectedLeadId] = useState<any>(null);
  const [selectedLeadName, setSelectedLeadName] = useState<string>("");
  const [viewTooltip, setViewTooltip] = useState<boolean>(true);

  const breadcrumbLink = [
    {
      label: "Partner List",
      link: routes.PARTNERLIST,
    },
    {
      label: tenantData?.basic_details?.company_name,
      handleClick: () => {
        navigate(routes.PARTNERDETAILS, {
          state: {
            partnerDetails: tenantData,
            isFromPartnerList: true,
          },
        });
      },
    },
    {
      label: "Leads Log",
      handleClick: () => {},
    },
  ];
  const [crumbMappingOption, setCrumbMappingOption] =
    useState<any>(breadcrumbLink);

  const handleSearch = _.debounce((text: string) => {
    setSearchText(text);
    setCurrentPage(1);
  }, 500);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const headers = [
    { key: "firstName", label: "First Name" },
    { key: "lastName", label: "Last Name" },
    { key: "email", label: "Email" },
    { key: "dateAssigned", label: "Date Assigned" },
    { key: "products", label: "Products" },
    { key: "leadValue", label: "Lead Value" },
    {
      key: "leadStatus",
      label: "Lead Status",
      render: (row: any) => <LeadsStatus status={row.leadStatus} />,
    },
  ];

  const fetchLeads = async (
    page = 1,
    search: string = "",
    filters: any = {}
  ) => {
    setIsLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      let params: any = {
        limit: pageSize,
        offset: offset,
        assignedToPartnerId: tenantData.id,
      };
      if (search) {
        params.searchText = search;
      }
      if (filters) {
        params = {
          ...params,
          ...filters,
        };
      }
      if (filters?.onBoardedAtStart) {
        params.assignedAtFromDate = dayjs(filters?.onBoardedAtStart);
      }
      if (filters?.onBoardedAtEnd) {
        params.assignedAtToDate = dayjs(filters?.onBoardedAtEnd);
      }
      delete params.onBoardedAtStart;
      delete params.onBoardedAtEnd;

      const response = await getLeads(params, "Assigned Leads");
      setLeadData(response.data.data.leads);
      setTotalRecords(response.data.data.totalRecords);
    } catch (error: any) {
      setAlertMessage({
        open: true,
        message: error?.response?.data?.message || "Failed to fetch campaigns",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!viewOpen) {
      fetchLeads(currentPage, searchText, filters);
    }
  }, [currentPage, searchText, sortOption, filters, viewOpen]);

  const formattedData = useMemo(() => {
    if (Array.isArray(leadData)) {
      return leadData.map((item, index) => {
        const leadValue = item.lead_value
          ? new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
            }).format(item.lead_value)
          : "-";
        const products = item?.interested_products || "-";
        const formattedDateAssigned = item?.assignedLead
          ? formatDate(item?.assignedLead[0]?.assigned_at)
          : "-";

        return {
          id: item.id || index,
          firstName: item.first_name || "-",
          lastName: item.last_name || "-",
          fullName: `${item.first_name || ""} ${item.last_name || ""}`,
          email: item.primary_email_address || "-",
          dateAssigned: formattedDateAssigned,
          products,
          leadValue,
          leadStatus: item.lead_status || "-",
          data: item,
        };
      });
    }
    return [];
  }, [leadData]);

  const handleViewClick = async (leadId: number, leadName: string) => {
    setSelectedLeadId(leadId);
    setSelectedLeadName(leadName);
    setCrumbMappingOption([
      {
        label: "Partner List",
        link: routes.PARTNERLIST,
      },
      {
        label: tenantData?.basic_details?.company_name,
        handleClick: () => {
          navigate(routes.PARTNERDETAILS, {
            state: {
              partnerDetails: tenantData,
              isFromPartnerList: true,
            },
          });
        },
      },
      {
        label: "Lead Logs",
        link: routes.PARTNERLEADSLOG,
        handleClick: (event: any) => {
          event.preventDefault();
          setCrumbMappingOption(breadcrumbLink);
          setViewOpen(false);
          setCurrentPage(1);
        },
      },
      { label: leadName, link: "" },
    ]);

    setViewOpen(true);
  };

  const handleLeadNameUpdate = (newName: string) => {
    setSelectedLeadName(newName);
    setCrumbMappingOption((prev: any) => {
      const updated = [...prev];
      updated[2] = { ...updated[2], label: newName };
      return updated;
    });
  };

  const noResultText = useMemo(() => {
    if (isLoading) {
      return "Loading Leads Log...";
    }
    if (searchText) {
      return "No Leads Log found";
    }
    return "No Leads Log available";
  }, [isLoading, searchText]);

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    const dateFilterActive =
      filters?.onBoardedAtEnd || filters?.onBoardedAtStart;

    const leadValueFilter = filters?.leadValueMin || filters?.leadValueMax;

    const otherFilters = { ...filters };
    delete otherFilters.onBoardedAtEnd;
    delete otherFilters.onBoardedAtStart;
    delete otherFilters.leadValueMin;
    delete otherFilters.leadValueMax;

    const otherActiveFilters = Object.values(otherFilters).filter(
      (val) =>
        val !== undefined &&
        val !== "" &&
        val !== null &&
        !(Array.isArray(val) && val.length === 0)
    ).length;

    if (dateFilterActive) {
      count += 1;
    }
    if (leadValueFilter) {
      count += 1;
    }

    count += otherActiveFilters;
    return count;
  }, [filters]);

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <>
      <Box className="leads-log-wrapper leadlist-wrapper">
        <Box className="breadcrumb-wrapper">
          <BreadcrumbComponent
            breadcrumbLink={crumbMappingOption}
            showBackArrow
            handleBackArrowClick={() => navigate(routes.PARTNERLIST)}
          />
        </Box>
        <div style={{ display: !viewOpen ? "block" : "none" }}>
          <Box>
            <div className="leads-log-filter-wrapper">
              <div className="search-input-wrapper">
                <SearchInputComponent
                  placeholder="Search by first name, last name, email etc.,"
                  onSearch={handleSearch}
                />
              </div>
              <div
                className="leads-log-assign-text"
                style={{ display: viewTooltip ? "flex" : "none" }}
              >
                <img src={TooltipIcon} />
                <div>
                  <div className="tooltip-text">
                    <div className="tooltip-text-close">
                      <span>
                        A list of all leads that have been assigned by AFP to
                        your Funeral Home.
                      </span>
                      <CloseIcon
                        fontSize="small"
                        onClick={() => setViewTooltip(false)}
                        style={{
                          cursor: "pointer",
                          width: "15px",
                          height: "15px",
                          color: "#AEAEAE",
                        }}
                      />
                    </div>
                    <span>
                      {" "}
                      To view all leads for your funeral home, navigate to the
                      Lead management module
                    </span>
                  </div>
                </div>
              </div>
              <div className="filter-right-section d-flex">
                <Badge
                  badgeContent={appliedFilterCount}
                  invisible={appliedFilterCount === 0}
                  color="secondary"
                  overlap="circular"
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  sx={{
                    "& .MuiBadge-badge": {
                      top: "1px",
                      right: "14px",
                      transform: "scale(1)",
                    },
                  }}
                >
                  <div
                    className={`filter-icon ${isFilterOpen ? "active-filter" : ""}`}
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    style={{ cursor: "pointer" }}
                  >
                    <img src={icons.FilterIcon} alt="filter-icon" />
                  </div>
                </Badge>
              </div>
            </div>
          </Box>
     
            <div className="leads-log-table-filter">
              <div className="leads-log-table">
                <MUIBoxTable
                  columns={headers}
                  data={formattedData}
                  noResultText={noResultText}
                  onRowClick={(rowData: any) =>
                    handleViewClick(rowData.id, rowData.fullName)
                  }
                />
              </div>
              {isFilterOpen ? (
                <LeadsLogFilters
                  open={isFilterOpen}
                  filters={filters}
                  onClose={() => setIsFilterOpen(false)}
                  onApplyFilters={(filterData, shouldClose = true) => {
                    try {
                      setFilters(filterData);
                      if (shouldClose) setIsFilterOpen(false);
                      setCurrentPage(1);
                    } catch (error: any) {
                      console.error("Failed to apply filters:", error);
                      setAlertMessage({
                        open: true,
                        message: "Failed to apply filters. Please try again.",
                        type: "error",
                      });
                    }
                  }}
                />
              ) : (
                <></>
              )}
            </div>
          {totalRecords && totalRecords > 0 ? (
            <div className="leads-pagination-container">
              <PaginationComponent
                totalRecords={totalRecords}
                pageSize={pageSize}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
              </div>
            ) : (
              <></>
            )}
   
        </div>
        {viewOpen && (
          <LeadView
            setLoading={setIsLoading}
            selectedLeadId={selectedLeadId}
            onLeadNameUpdate={handleLeadNameUpdate}
          />
        )}
      </Box>
      {alertMessage.open && (
        <AlertComponent
          message={alertMessage.message}
          type={alertMessage.type || "info"}
          open={alertMessage.open}
          onClose={() => setAlertMessage({ open: false })}
        />
      )}
    </>
  );
};
export default LeadsLog;
